import React from 'react';
import { useDispatch } from 'react-redux';
import { handle400Statuses, sendMultipartApiRequest } from '@services/api';
import { uploadFilesActions } from '@constants/actionTypes';
import { FILE_UPLOAD_STATUS } from '@hooks/features/useUploadFormFiles';
import { addFileDefaults } from '@utils/doc';
import { logNetRequest } from '@utils/usefulFuncs';
import { Body5, Container } from '@ui';
import { TransparentFlatButton } from '@ui/buttons';
import { ProgressOverlay } from '@ui/ProgressOverlay';
import { DragDrop } from '@ui/DragAndDropUploader';
import { CustomUploadFilesList } from '@containers/DigitalAnalyst/UploadFilesList';
import { FormWrapper } from '../styles';
import { getTemplateBinaryConfig } from './templateBinaryUtils';

function UploadingOverlay() {
  return <ProgressOverlay message="Uploading..." />;
}

function ReplaceTemplateUploader({ template, onUploadComplete }) {
  const dispatch = useDispatch();
  const [isSubmittingToApi, setIsSubmittingToApi] = React.useState(false);
  const [isCompleted, setIsCompleted] = React.useState(false);
  const replaceTemplateConfig = React.useMemo(() => getTemplateBinaryConfig(template), [template]);

  React.useEffect(() => {
    return () => {
      dispatch({
        type: uploadFilesActions.clear,
      });
    };
  }, [dispatch]);

  const handleAddFiles = (fileOrFiles) => {
    const extraParams = {
      generate_template_spec: true,
    };

    dispatch({
      type: uploadFilesActions.replaceFile,
      payload: addFileDefaults(fileOrFiles, extraParams),
    });
  };

  const handleUpload = async (files) => {
    setIsSubmittingToApi(true);

    const url = `v1/genai/templates/${template.id}/${replaceTemplateConfig.endpointSuffix}`;
    const file = files[0];
    const formData = new FormData();
    const { type, name } = file;

    formData.append('name', name);
    formData.append('extract_formula_dependencies', true);
    formData.append('generate_template_spec', true);
    formData.append(type, file.file);

    dispatch({
      type: uploadFilesActions.updateFiles,
      payload: [
        {
          prevName: name,
          changes: {
            status: FILE_UPLOAD_STATUS.uploading,
            error: null,
          },
        },
      ],
    });

    const response = await sendMultipartApiRequest('post', url, formData);
    handle400Statuses(response?.status);
    logNetRequest(url, response?.status, response?.data, response?.error);

    const wasSuccess = response?.status === 200;
    const errorMessage = wasSuccess
      ? null
      : response?.status === 413
      ? 'File is too large'
      : response?.data?.error || 'Something went wrong, please try again.';

    dispatch({
      type: uploadFilesActions.updateFiles,
      payload: [
        {
          prevName: name,
          changes: {
            status: wasSuccess ? FILE_UPLOAD_STATUS.complete : FILE_UPLOAD_STATUS.failed,
            error: errorMessage,
            id: response?.data?.doc_id || null,
          },
        },
      ],
    });

    setIsSubmittingToApi(false);

    if (wasSuccess) {
      setIsCompleted(true);
      onUploadComplete?.();
    }

    return wasSuccess;
  };

  if (isCompleted) {
    return (
      <Container top={24}>
        <Body5 bold>Template replaced successfully</Body5>
      </Container>
    );
  }

  return (
    <>
      {isSubmittingToApi && <UploadingOverlay />}
      <Container>
        <FormWrapper>
          <Container top={24}>
            <Body5>{replaceTemplateConfig.helperText}</Body5>
          </Container>
          <Container top={32}>
            <DragDrop onAddFiles={handleAddFiles} {...replaceTemplateConfig.dragDropProps} />
          </Container>
          <Container top={16}>
            <CustomUploadFilesList onUpload={handleUpload} isUploading={isSubmittingToApi} />
          </Container>
        </FormWrapper>
      </Container>
    </>
  );
}

export function ReplaceTemplate({ template, onCancel, onUploadComplete }) {
  const replaceTemplateConfig = React.useMemo(() => getTemplateBinaryConfig(template), [template]);

  return (
    <Container top={24}>
      <Body5 bold>Replace {replaceTemplateConfig.label} Template</Body5>
      <Container>
        <ReplaceTemplateUploader template={template} onUploadComplete={onUploadComplete} />
      </Container>
      <Container top={16}>
        <TransparentFlatButton onClick={onCancel}>Back To Actions</TransparentFlatButton>
      </Container>
    </Container>
  );
}
